import { Matrix4, Mesh, Vector3 } from 'three';

import { FatLine } from '@/lib/three';
import { Rawdata } from '@/types';

import { Circle2d, Cylinder3d, Ring2d, Sphere3d } from '../annos';
import { Line3d } from '../annos/Line3d';
import { EnhancedObject3D } from '../graphics';

const DEFAULT_COLOR = '#FFEFA3';
const INNER_RADIUS = 4;
const OUTER_RADIUS = 16;
const INNER_RADIUS_RADIO = 0.004;
const OUTER_RADIUS_RADIO = 0.016;

// 观察点数据格式
export interface IKeyPoint {
  name: string; // 唯一标识 uuid 格式
  coord2: [number, number]; // [x, y] 相对于图像坐标系
  coord3: number[]; // [x1, y1, z1, x2, y2, z2...] 相对于点云坐标系
  color: string; // 颜色
}

// 观察点的创建与更新
export class KeyPoint {
  /**
   * 创建关键点的渲染对象
   * @param data IKeyPoint
   * @param file // transMatrix: 文件坐标系 到 threejs 坐标系的转换，若无则表示不需要转换
   * @returns
   */
  static create(
    data: Partial<IKeyPoint>,
    file: {
      type: Rawdata['type'];
      transMatrix?: Matrix4;
      width?: number;
    }
  ) {
    const { type, transMatrix, width } = file;
    const { name, coord2 = [0, 0], coord3 = [0, 0, 0], color: hexColor = DEFAULT_COLOR } = data;

    let target: FatLine | Mesh = new Mesh();

    if (type === 'pointcloud') {
      target = Line3d.create(coord3, { color: hexColor });
      const startPoint = Sphere3d.create([...coord3.slice(0, 3), 0.08], { color: hexColor });

      // coord3 的排列顺序[...startPoint, ...endPoint, ...keypoint], keypoint 为圆柱体的顶点，可能不存在
      if (coord3.length === 9) {
        const radius = 0.4,
          height = 0.2;
        const cylinder = Cylinder3d.create([coord3[6], coord3[7], coord3[8], radius, radius, height], {
          color: hexColor,
        });
        // 旋转圆柱体跟线保持方向一致
        cylinder.lookAt(coord3[0], coord3[1], coord3[2]);
        cylinder.rotateX(Math.PI / 2);
        target.add(cylinder);
      }

      target.add(startPoint);
    } else {
      const innerRadius = width ? width * INNER_RADIUS_RADIO : INNER_RADIUS;
      const outerRadius = width ? width * OUTER_RADIUS_RADIO : OUTER_RADIUS;

      const ring = Ring2d.create([0, 0, outerRadius, innerRadius], {
        color: hexColor,
        opacity: 1,
      });
      const innerCircle = Circle2d.create([0, 0, innerRadius], { color: hexColor, opacity: 1 });

      target = Circle2d.create([0, 0, outerRadius], { color: hexColor, opacity: 0.5 });
      target.add(ring, innerCircle);

      target.position.setX(coord2[0]);
      target.position.setY(coord2[1]);
    }

    target.name = name ?? target.uuid;

    if (transMatrix) {
      target.applyMatrix4(transMatrix);
    }

    target.userData.type = 'keypoint';

    return target;
  }

  static update(
    target: FatLine | Mesh,
    data: Partial<IKeyPoint>,
    file: {
      type: Rawdata['type'];
      transMatrix: Matrix4; // 文件坐标系 到 threejs 坐标系的转换
    }
  ) {
    const { type, transMatrix } = file;
    const { coord2 = [0, 0], coord3 = [0, 0, 0] } = data;
    if (type === 'pointcloud' && target instanceof FatLine) {
      if (data.color && data.color !== target.userData.color) {
        EnhancedObject3D.setObjectColor(target, data.color);
        target.userData.color = data.color;
      }
      Line3d.update(target, coord3);
    } else {
      const newPos = new Vector3(...coord2, 0).applyMatrix4(transMatrix);
      target.position.setX(newPos.x);
      target.position.setY(newPos.y);
    }
  }
}
